/**
 * Block-document model.
 *
 * A page body is an ordered array of blocks, each `{id, type, data}`. This
 * module owns every mutation of that array — create, move, duplicate, remove,
 * normalise — as pure functions over plain data.
 *
 * Layer rule: no React, no CSS, no knowledge of how a block *looks*. The editor
 * calls these to change the document; the renderer decides appearance. Swapping
 * the theme cannot break anything here.
 *
 * 块文档模型。
 *
 * 页面正文是一个有序块数组，每块形如 {id, type, data}。本模块以纯函数的形式
 * 拥有对该数组的全部修改操作——新建、移动、复制、删除、规范化。
 *
 * 分层规则：不含 React、不含 CSS、不知道块「长什么样」。编辑器调用这些函数改文档，
 * 渲染器决定外观。换主题不会影响这里的任何逻辑。
 */

import { BLOCK_TYPES, TEMPLATES, emptyI18n } from '../../shared/schema.js'

/**
 * Generate a collision-resistant local id.
 *
 * Blocks are reordered by drag, so React needs a stable key that survives a
 * move. Array index will not do.
 *
 * 生成不易冲突的本地 id。块可拖拽重排，React 需要一个在移动后依然稳定的 key，
 * 数组下标不行。
 *
 * @returns {string} Block id / 块 id。
 */
export function newBlockId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `b_${crypto.randomUUID().slice(0, 12)}`
  return `b_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`
}

/**
 * Build the default `data` payload for a block type from its field declarations.
 *
 * 按字段声明构造某块类型的默认 data。
 *
 * @param {string} type Block type key / 块类型键名。
 * @returns {object} Default data / 默认数据。
 */
export function defaultBlockData(type) {
  const spec = BLOCK_TYPES[type]
  if (!spec) return {}
  const data = {}
  for (const field of spec.fields) {
    if (field.default !== undefined) data[field.key] = field.default
    else if (field.i18n) data[field.key] = emptyI18n()
    else if (field.type === 'uploadList') data[field.key] = []
    else if (field.type === 'boolean') data[field.key] = false
    else data[field.key] = ''
  }
  return data
}

/**
 * Create a block, optionally seeded with partial data.
 *
 * 新建一个块，可用部分数据预填。
 *
 * @param {string} type Block type key / 块类型键名。
 * @param {object} [data] Partial data merged over the defaults / 覆盖默认值的部分数据。
 * @returns {{id: string, type: string, data: object}} New block / 新块。
 */
export function createBlock(type, data = {}) {
  return { id: newBlockId(), type, data: { ...defaultBlockData(type), ...data } }
}

/**
 * Instantiate a preset template into fresh blocks.
 *
 * Templates in the schema carry no ids (they are static data); this stamps a
 * new id on every block so two documents from the same template stay distinct.
 *
 * 把预设模板实例化成新块。schema 里的模板不带 id（它们是静态数据），
 * 这里为每个块打上新 id，保证同一模板生成的两份文档互不干扰。
 *
 * @param {string} templateKey Key in TEMPLATES / 模板键名。
 * @returns {Array<object>} Blocks / 块数组。
 */
export function instantiateTemplate(templateKey) {
  const template = TEMPLATES[templateKey]
  if (!template) return []
  return template.blocks.map((block) => createBlock(block.type, block.data))
}

/**
 * Templates offered for a collection, narrowed to one block document.
 *
 * A collection with several block documents (research: project + demo) wants a
 * different starting shape in each, so a template may declare a `group`. A
 * template with no group fits any document in its collection, and the blank
 * template fits everywhere.
 *
 * 某集合可用的模板，并按具体的块文档收窄。
 *
 * 带多份块文档的集合（research 的「项目」+「Demo」）在每一份里想要的起始结构不同，
 * 因此模板可以声明 group。没有 group 的模板适用于该集合的任何一份文档，
 * 空白模板则处处适用。
 *
 * @param {string} collectionKey Collection key / 集合键名。
 * @param {string} [group] Block-document group to narrow to / 用于收窄的块文档分组。
 * @returns {Array<{key: string, template: object}>} Template entries / 模板条目。
 */
export function templatesFor(collectionKey, group) {
  return Object.entries(TEMPLATES)
    .filter(([, template]) => template.collection === collectionKey || template.collection === '*')
    .filter(([, template]) => !template.group || !group || template.group === group)
    .map(([key, template]) => ({ key, template }))
}

/**
 * Insert a block at a position (clamped to the array bounds).
 *
 * 在指定位置插入块（位置会被限制在数组范围内）。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {object} block Block to insert / 待插入的块。
 * @param {number} [at] Index; -1 or omitted appends / 下标，-1 或省略表示追加。
 * @returns {Array<object>} New array / 新数组。
 */
export function insertBlock(blocks, block, at = -1) {
  const next = [...blocks]
  const index = at < 0 || at > next.length ? next.length : at
  next.splice(index, 0, block)
  return next
}

/**
 * Replace one block's data, leaving id and type intact.
 *
 * 替换某块的 data，保持 id 与 type 不变。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {string} id Block id / 块 id。
 * @param {object} patch Partial data merged in / 合并进去的部分数据。
 * @returns {Array<object>} New array / 新数组。
 */
export function updateBlockData(blocks, id, patch) {
  return blocks.map((block) => (block.id === id ? { ...block, data: { ...block.data, ...patch } } : block))
}

/**
 * Move a block by a signed offset, clamped to the array bounds.
 *
 * 按带符号偏移量移动块，位置限制在数组范围内。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {string} id Block id / 块 id。
 * @param {number} offset -1 for up, +1 for down / -1 上移，+1 下移。
 * @returns {Array<object>} New array / 新数组。
 */
export function moveBlock(blocks, id, offset) {
  const from = blocks.findIndex((block) => block.id === id)
  if (from < 0) return blocks
  const to = Math.min(blocks.length - 1, Math.max(0, from + offset))
  if (to === from) return blocks
  const next = [...blocks]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * Move a block to an absolute index (used by drag-and-drop).
 *
 * 把块移动到绝对下标（拖拽排序用）。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {number} from Source index / 源下标。
 * @param {number} to Target index / 目标下标。
 * @returns {Array<object>} New array / 新数组。
 */
export function reorderBlocks(blocks, from, to) {
  if (from === to || from < 0 || from >= blocks.length) return blocks
  const next = [...blocks]
  const [moved] = next.splice(from, 1)
  next.splice(Math.min(Math.max(to, 0), next.length), 0, moved)
  return next
}

/**
 * Remove a block.
 *
 * 删除一个块。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {string} id Block id / 块 id。
 * @returns {Array<object>} New array / 新数组。
 */
export function removeBlock(blocks, id) {
  return blocks.filter((block) => block.id !== id)
}

/**
 * Duplicate a block directly below itself.
 *
 * 在原块下方复制一份。
 *
 * @param {Array<object>} blocks Current blocks / 当前块数组。
 * @param {string} id Block id / 块 id。
 * @returns {Array<object>} New array / 新数组。
 */
export function duplicateBlock(blocks, id) {
  const index = blocks.findIndex((block) => block.id === id)
  if (index < 0) return blocks
  const copy = { ...blocks[index], id: newBlockId(), data: structuredClone(blocks[index].data) }
  return insertBlock(blocks, copy, index + 1)
}

/**
 * Repair a block array loaded from the server.
 *
 * Older records may predate a block type or lack ids. Rather than crash the
 * renderer we drop unknown types and backfill everything else, so an editor can
 * always open and re-save a legacy document.
 *
 * 修复从服务端读回的块数组。旧记录可能早于某个块类型，或者缺少 id。与其让渲染器
 * 崩溃，不如丢弃未知类型、补齐其余字段——这样编辑总能打开并重新保存历史文档。
 *
 * @param {any} blocks Untrusted blocks value / 不受信任的块数据。
 * @returns {Array<object>} Safe block array / 安全的块数组。
 */
export function normaliseBlocks(blocks) {
  if (!Array.isArray(blocks)) return []
  return blocks
    .filter((block) => block && typeof block === 'object' && BLOCK_TYPES[block.type])
    .map((block) => ({
      id: block.id || newBlockId(),
      type: block.type,
      data: { ...defaultBlockData(block.type), ...(block.data || {}) },
    }))
}

/**
 * True when a block has nothing worth rendering.
 *
 * Used by the renderer to skip placeholder blocks a template inserted but the
 * author never filled in — an empty template slot should not leave a hole on
 * the public page.
 *
 * 判断块是否没有可渲染的内容。渲染器用它跳过模板插入但作者没填的占位块——
 * 空的模板槽不该在公开页留下一个洞。
 *
 * @param {object} block Block to test / 待判断的块。
 * @returns {boolean} True when empty / 为空则 true。
 */
export function isBlockEmpty(block) {
  if (!block) return true
  if (block.type === 'divider') return false

  const values = Object.values(block.data || {})
  return !values.some((value) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim() !== ''
    if (typeof value === 'number') return true
    if (typeof value === 'boolean') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') {
      return Object.values(value).some((inner) =>
        Array.isArray(inner) ? inner.length > 0 : String(inner ?? '').trim() !== ''
      )
    }
    return false
  })
}

/**
 * Estimate reading time, counting CJK by character and Latin by word.
 *
 * 估算阅读时间：中文按字数、拉丁文按词数计。
 *
 * @param {Array<object>} blocks Block array / 块数组。
 * @param {'zh'|'en'} lang Language to measure / 计量语言。
 * @returns {number} Minutes, minimum 1 / 分钟数，最少 1。
 */
export function readingMinutes(blocks, lang = 'zh') {
  let cjk = 0
  let latin = 0

  const visit = (value) => {
    if (typeof value === 'string') {
      cjk += (value.match(/[⺀-鿿]/g) || []).length
      latin += (value.match(/[A-Za-z]+/g) || []).length
      return
    }
    if (Array.isArray(value)) return value.forEach(visit)
    if (value && typeof value === 'object') {
      if ('zh' in value || 'en' in value) return visit(value[lang] ?? value.zh ?? value.en ?? '')
      return Object.values(value).forEach(visit)
    }
  }

  normaliseBlocks(blocks).forEach((block) => visit(block.data))
  return Math.max(1, Math.round(cjk / 400 + latin / 220))
}
